<template>
  <div :class="`autocomplete scrollable ${className}`">
    <div>
      <label v-if="useLabel" :for="id">{{ labelText }}</label>
      <input
        :id="id"
        type="text"
        class="form-input"
        :placeholder="placeholderText"
        v-model="searchTerm"
        @input="(e) => (searchTerm = e.target.value)"
        v-on:keyup="handleKeyPress($event)"
        v-on:keydown="handleKeyDown($event)"
        v-on:focus="inputFocus($event)"
        aria-autocomplete="list"
        aria-describedby="autocomplete-instructions"
      />
      <span id="autocomplete-instructions" class="screen-reader-only">
        Kenttä ehdottaa tuloksia. Valitse arvo listalta nuolinäppäimillä ja paina Enter.
      </span>
    </div>
    <div
      :id="resultsId"
      :class="`autocomplete__results ${
        noRegionResults || autocompleteError || regionResults.length < 6 ? 'auto-height' : ''
      }`"
      v-if="
        (noRegionResults && searchTerm.length > 1) ||
        regionResults.length > 0 ||
        postalCodeResults.length > 0 ||
        (autocompleteError && searchTerm.length > 1) ||
        (noListingFound && searchTerm.length > 5)
      "
    >
      <!-- These two divs are required by v-bar library -->
      <div class="vbar-div">
        <div class="vbar-inner-div" id="scroll-box">
          <!-- The actual content -->
          <div
            class="result postalCode selectable"
            v-for="result in postalCodeResults"
            :key="`postal-code-${result.code}`"
            @click.prevent="selectPostalCode(result.code)"
          >
            <span class="filter-value">{{ result.code }}, {{ result.office }}</span>
          </div>
          <div
            class="result city selectable"
            v-for="result in regionResults"
            :key="`region-${result.code}`"
            @click.prevent="selectLocation(result)"
          >
            <span class="filter-value">
              <span v-if="result.street">{{ result.street }},</span>
              <span v-if="result.district">{{ result.district }},</span>
              <span v-if="result.region">{{ result.region }},</span>
              <span v-if="result.county">{{ result.county }}</span>
              <span class="city">{{ result.city }}</span>
            </span>
            <span class="region-type">
              <span v-if="result.county">Maakunta</span>
              <span v-else-if="result.street">Katu</span>
              <span v-else-if="result.district">Kaupunginosa</span>
              <span v-else-if="result.region">Alue</span>
              <span v-else>Kaupunki</span>
            </span>
          </div>
          <div class="result empty" v-if="autocompleteError">
            <span class="filter-value">Haussa tapahtui virhe!</span>
          </div>
          <div class="result empty" v-else-if="noListingFound">
            <span class="filter-value">Kohdenumerolla ei löydy kohdetta.</span>
          </div>
          <div class="result empty" v-else-if="noRegionResults">
            <span class="filter-value">Ei hakutuloksia</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as actions from '@/app/store/action-types';
import { debounce } from 'lodash-es';
import { mapGetters } from 'vuex';
import utils from '@/app/utils/utils';
import { areWeInSearchPage } from '@/app/routes';
import config from '@/config';
import { listing } from '@/app/routes';

export default {
  name: 'autocomplete',
  props: {
    className: {
      type: String,
      required: false,
    },
    emptyInput: {
      type: Boolean,
      required: false,
    },
    id: {
      type: String,
      required: false,
    },
    resultsId: {
      type: String,
      required: false,
    },
    useLabel: {
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    searchTerm: '',
    streets: [],
    cities: [],
    counties: [],
    districts: [],
    regions: [],
    postalCodes: [],
    selectedItem: -1,
    autocompleteItemHeight: 52,
    autocompleteBoxHeight: 6 * 52,
    placeholderTextDesktop: 'Kaupunki, kohdenumero tai postinumero',
    placeholderTextSmallerThanDesktop: 'Kaupunki, kohdenumero tai postinumero',
    placeholderText: '',
    labelText: '',
  }),
  mounted() {
    this.searchLocations = debounce(this.searchLocations, 150);
    this.setPlaceholderText(window.innerWidth);
    this.setLabelText(window.innerWidth);

    this.$bus.$on(`windowResized-${this.$route.path}`, () => {
      this.setPlaceholderText(window.innerWidth);
      this.setLabelText(window.innerWidth);
    });
  },
  methods: {
    setLabelText(windowWidth) {
      if (this.useLabel) {
        this.labelText =
          windowWidth > config.BREAKPOINTS.HD_DESKTOP
            ? this.placeholderTextDesktop
            : this.placeholderTextSmallerThanDesktop;
      } else {
        this.labelText = '';
      }
    },
    setPlaceholderText(windowWidth) {
      if (this.useLabel) {
        this.placeholderText = '';
      } else {
        this.placeholderText =
          windowWidth > config.BREAKPOINTS.HD_DESKTOP
            ? this.placeholderTextDesktop
            : this.placeholderTextSmallerThanDesktop;
      }
    },
    inputFocus(event) {
      utils.inputFocusOnAndroid(event);
    },
    handleKeyDown(event) {
      // tab
      if (event.which === 9 && this.selectedItem !== -1) {
        this.selectItemByIndex(this.selectedItem);
      }
    },
    handleKeyPress(event) {
      switch (event.which) {
        // up arrow
        case 38:
          this.setSelectedItem(-1);
          this.trackScroll(-1);
          break;
        // down arrow
        case 40:
          this.setSelectedItem(1);
          this.trackScroll(1);
          break;
        // enter
        case 13:
          this.handleEnterPress(event);
          break;
        // tab
        case 9:
          // no-op
          break;
        default:
          this.searchLocations();
          break;
      }
    },
    selectItemByIndex(index) {
      const result = this.regionResults[index] || this.postalCodeResults[index];
      if (result.districtId || result.cityId || result.streetId) {
        this.selectLocation(result);
      } else {
        this.selectPostalCode(result.code);
      }
    },
    handleEnterPress() {
      const searchPage = areWeInSearchPage(this.$route.path);

      if (this.selectedItem !== -1) {
        this.selectItemByIndex(this.selectedItem);
      } else {
        if (
          this.regionResults.length > 0 &&
          this.districts.length === 0 &&
          this.counties.length === 0 &&
          this.cities.length === 0 &&
          this.regions.length === 0
        ) {
          this.selectLocation(this.regionResults[0]); // default to first result
        }

        if (this.postalCodeResults.length > 0 && this.postalCodes.length === 0) {
          this.selectPostalCode(this.postalCodeResults[0].code); // default to first result
        }
      }

      if (searchPage) {
        this.$bus.$emit('triggerSearchApartments');
      } else {
        const inputs = Array.from(document.querySelectorAll('input'));
        inputs[1].focus();
      }
    },
    setSelectedItem(increment) {
      const autocompleteItems = Array.from(document.querySelectorAll('.result.selectable'));
      if (!autocompleteItems.length) {
        return;
      }

      if (increment === 1) {
        this.selectedItem =
          this.selectedItem >= autocompleteItems.length - 1 || this.selectedItem === -1
            ? 0
            : (this.selectedItem += increment);
      } else {
        this.selectedItem = this.selectedItem <= 0 ? autocompleteItems.length - 1 : (this.selectedItem += increment);
      }
      autocompleteItems.forEach((item) => {
        item.classList.remove('selected-item');
      });
      autocompleteItems[this.selectedItem].classList.add('selected-item');
      this.searchTerm = autocompleteItems[this.selectedItem].getElementsByClassName('filter-value')[0].innerText;
    },
    trackScroll() {
      if (this.selectedItem === -1) {
        return;
      }
      const parent = document.getElementById('scroll-box');
      const offset = this.selectedItem * this.autocompleteItemHeight;

      if (offset < parent.scrollTop) {
        parent.scrollTop = offset;
      }

      if (offset >= parent.scrollTop + this.autocompleteBoxHeight) {
        parent.scrollTop = offset - this.autocompleteBoxHeight + this.autocompleteItemHeight;
      }
    },
    searchLocations() {
      // Clear previous results if we started typing again
      this.streets = [];
      this.districts = [];
      this.regions = [];
      this.counties = [];
      this.cities = [];
      this.postalCodes = [];
      this.$bus.$emit('clearAutocompleteLocations');

      // require at least one char before searching
      if (this.searchTerm.length > 1) {
        this.$store.dispatch(actions.FETCH_LOCATION_SUGGESTIONS, this.searchTerm);
      } else {
        this.$store.dispatch(actions.CLEAR_LOCATION_SUGGESTIONS);
        this.selectedItem = -1;
      }
    },
    selectPostalCode(code) {
      this.postalCodes.push(code);
      this.searchTerm = this.emptyInput ? '' : code;
      this.$bus.$emit('autocompletePostalCodeSelected', code);
      this.selectedItem = -1;
      this.$store.dispatch(actions.CLEAR_LOCATION_SUGGESTIONS);
    },

    selectLocation(result) {
      if (result.streetId) {
        this.streets.push(result);
        this.searchTerm = this.emptyInput ? '' : `${result.street}, ${result.city}`;
        this.$bus.$emit('locationFilterUpdated', {
          dataName: 'streets',
          city: result.city,
          street: result.street,
          streetId: result.streetId,
        });
        this.$bus.$emit('autocompleteStreetSelected', result);
      } else if (result.districtId) {
        this.districts.push(result);
        this.searchTerm = this.emptyInput ? '' : `${result.district}, ${result.city}`;
        this.$bus.$emit('locationFilterUpdated', {
          dataName: 'districts',
          city: result.city,
          district: result.district,
          districtId: result.districtId,
        });
        this.$bus.$emit('autocompleteDistrictSelected', result);
      } else if (result.region) {
        this.regions.push(result);
        this.searchTerm = this.emptyInput ? '' : `${result.region}, ${result.city}`;
        this.$bus.$emit('locationFilterUpdated', {
          dataName: 'regions',
          city: result.city,
          region: result.region,
          regionId: result.regionId,
        });
        this.$bus.$emit('autocompleteRegionSelected', result);
      } else if (result.cityId) {
        this.cities.push(result);
        this.searchTerm = this.emptyInput ? '' : result.city;
        this.$bus.$emit('locationFilterUpdated', { dataName: 'cities', city: result.city, cityId: result.cityId });
        this.$bus.$emit('autocompleteCitySelected', result);
      } else if (result.countyId) {
        this.counties.push(result);
        this.searchTerm = this.emptyInput ? '' : result.county;
        this.$bus.$emit('locationFilterUpdated', {
          dataName: 'counties',
          county: result.county,
          countyId: result.countyId,
        });
        this.$bus.$emit('autocompleteCountySelected', result);
      }
      this.selectedItem = -1;
      this.$store.dispatch(actions.CLEAR_LOCATION_SUGGESTIONS);
    },
  },
  watch: {
    listingFound(listingId) {
      void this.$router.push(`${listing.path}/${listingId}`);
      this.$bus.$emit('autocompleteListingFound', listingId);
    },
  },
  computed: {
    ...mapGetters({
      regionResults: 'regionResults',
      postalCodeResults: 'postalCodeResults',
      noRegionResults: 'noRegionResults',
      autocompleteError: 'autocompleteError',
      noListingFound: 'noListingFound',
      listingFound: 'listingFound',
    }),
  },
  beforeUnmount() {
    this.$store.dispatch(actions.CLEAR_LOCATION_SUGGESTIONS);
  },
};
</script>
